import type { LeaseRejectionCode } from '@repo/desktop-bridge';

/**
 * What the *device* said when it was handed a lease, in words.
 *
 * The same separation `error-messages.ts` makes for the API's codes and
 * `till-messages.ts` makes for the till's: a screen looks a refusal up, it does
 * not word one. These are neither — every code here is decided on the machine,
 * by `verifyLease`, against bytes the API has already sent. **The registration
 * itself succeeded** when one of these comes back: a lease exists, the terminal
 * is in service on the server, and what failed is the last step, putting it on
 * this device.
 *
 * The audience is an owner standing at the terminal setting it up, not a cashier
 * mid-queue. So each says which of the two machines is wrong — the terminal's
 * software, its clock, its configuration — because that is the only thing that
 * decides who fixes it, and none of them says "try again" where trying again
 * cannot work.
 */
const LEASE_MESSAGES: Record<LeaseRejectionCode, string> = {
  /**
   * The two version-skew refusals, and they are the same conversation: this
   * shell is older than the server that signed the lease. Named as the terminal
   * being behind rather than as a bad lease, because the lease is fine and it is
   * this machine that has to change.
   */
  LEASE_MALFORMED:
    'This terminal could not read the lease it was given. Its software is likely older than the server. Update the terminal, then register it again.',

  LEASE_UNSUPPORTED_ALGORITHM:
    'The lease is signed in a way this terminal does not know how to check. Its software is older than the server. Update the terminal, then register it again.',

  /**
   * Signed by a key this device has no record of. Almost always a terminal
   * pointed at a different API than the one the back office is talking to, which
   * is a configuration question rather than a fault.
   */
  LEASE_KEY_MISMATCH:
    'The lease was signed by a server this terminal does not recognise. Check the terminal is configured for this park’s API, then register it again.',

  /**
   * The one that is not a mistake. The bytes verified against the right key and
   * did not match, which means they changed between the server and here — so it
   * names the connection rather than suggesting a retry that would fail the same
   * way, or worse, succeed with something altered.
   */
  LEASE_SIGNATURE_INVALID:
    'The lease did not verify: it was altered between the server and this terminal. Do not retry on this connection — tell whoever runs the park network.',

  /**
   * A lease is 72 hours old at the outside and this one was already past its
   * end when it arrived, so either it sat somewhere for three days or this
   * machine's clock is wrong. The clock is named first because it is the common
   * one and the one that will do this again tomorrow.
   */
  LEASE_EXPIRED:
    'The lease had already run out when this terminal received it. Check the machine’s date and time are right, then register it again.',

  /**
   * Not reachable from registering — this is the renewal guard, refusing a lease
   * that names a different terminal or outlet. Worded for what it would mean
   * here anyway, because a `Record` with a hole in it is a runtime `undefined`
   * on a screen.
   */
  LEASE_TERMINAL_MISMATCH:
    'This lease names a different terminal. Register the machine against its own row in this list, not another one.',

  /**
   * Not a failure and deliberately not worded as one. The device already holds a
   * newer lease, so it refused to move backwards — and it is still registered,
   * still working, and nothing needs doing.
   */
  LEASE_SUPERSEDED:
    'This terminal already holds a newer lease, so it kept the one it has. It is registered and nothing needs doing.',
};

/** The sentence for a refusal the device raised. */
export const messageForLeaseRejection = (code: LeaseRejectionCode): string => LEASE_MESSAGES[code];
